import { Component } from 'react';
import ComponentName from '../constant/ComponentName';
import { notUndefined } from '../util/Assert';
import { FormicaProps } from './withFormica';
import { AuditClient } from '../client/audit/AuditClient';
import GatewayContainer from 'config/GatewayContainer';
import GatewayNames from 'constant/GatewayNames';
import { query } from '@trlogic/client-js';
import NavigationUtil from '../util/NavigationUtil';
import { AuditLog } from '../domain/rbac/RbacDomains';
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;
import { RootState } from '../redux/store';
import { enterComponent, leaveComponent } from '../redux/actions/ComponentActions';
import { connect, ConnectedProps } from 'react-redux';
import RoleHelper from '../RoleHelper';
import { clearPage } from '../redux/actions/PageActions';
import { CustomTabItem } from '../components/custom/CustomTabv2';
import EnvironmentUtil from '../util/EnvironmentUtil';
import { InstanceContainer } from 'config/binding/InstanceBinding';
import Injectables from '../constant/Injectables';

abstract class StandardEditorContainerComponent<TProps extends FormicaProps, TState extends DefaultState, TItem> extends Component<
  TProps & DefaultProps,
  TState
> {
  protected readonly args: StandardEditorContainerComponentArgs;
  protected readonly preview:
    | {
        id: string;
      }
    | undefined;

  private readonly _auditLogService: AuditClient = GatewayContainer.getInstance().get(GatewayNames.AUDIT_CLIENT);
  protected readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);

  protected constructor(props: TProps & DefaultProps, args: StandardEditorContainerComponentArgs) {
    super(props);
    notUndefined(args, 'Args cannot be undefined');
    this.args = args;
    this.preview = this.getPreviewArgs();
  }

  public async componentDidMount() {
    const { enterComponent } = this.props;
    const id = this.getItemIdFromRoute();
    if (!this.preview?.id) {
      enterComponent({ component: this.args.component, id: id });
    }
    await this.startTracker();
  }

  public async componentWillUnmount() {
    const { leaveComponent, clearPage } = this.props;
    RoleHelper.clearCurrentAccess();
    clearPage();
    leaveComponent();
    await this.stopTracker();
  }

  protected startTracker = async () => {
    if (this.args.canPreview && this.preview) {
      const log = await this.getAuditLog();
      if (log != undefined) {
        RoleHelper.freeze();
        this._preparePreview(log);
      }
    } else {
      this._startTracker();
    }
  };

  protected stopTracker = async () => {
    await this._stopTracker();
  };

  protected getItemIdFromRoute = () => this.props.router.params.id;

  private getPreviewArgs = () => {
    const { router } = this.props;
    if (this.args.canPreview == false) {
      return;
    }
    const urlSearchParams = new URLSearchParams(router.location.search);
    const preview: string | null = urlSearchParams.get('preview');
    const id: string | null = urlSearchParams.get('id');
    if (preview == null || id == null || id.trim().length == 0) return;
    return {
      id: id!,
    };
  };

  protected getAuditLog = async (): Promise<AuditLog | undefined> => {
    try {
      const id = this.preview?.id;
      if (id == undefined) return;
      const endpoint = `audit_log_ui_${NavigationUtil.getTenant()}_*`;
      const _query = QueryBuilder.select().from(endpoint).where(eq('_id', id)).build();
      const log = await this._auditLogService.getAuditLog(_query);
      return log?.hits?.hits?.[0]?._source;
    } catch (e) {}
  };

  protected async _preparePreview(log: AuditLog): Promise<void> {
    const document = JSON.parse(log.document.data);
    if (document == undefined || typeof document != 'object') {
      console.error('Document is not valid', document);
      return;
    }
    this.changeItem(document);
    this.setState({ loading: false });
  }

  protected abstract _startTracker(): void | Promise<void>;

  protected abstract _stopTracker(): void | Promise<void>;

  protected abstract getTabs(): CustomTabItem[];

  protected abstract tracker(): void | Promise<void>;

  protected abstract changeItem(item: TItem): void;
}

const mapStateToProps = (state: RootState) => ({});
const mapDispatchToProps = { enterComponent, leaveComponent, clearPage };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export interface DefaultProps extends ReduxProps, FormicaProps {}

interface DefaultState {
  loading: boolean;
}

export interface StandardEditorContainerComponentArgs {
  component: ComponentName;
  canPreview?: boolean;
  icon: string;
}

export default StandardEditorContainerComponent;
